import {FluxAvatar} from "@buildwithflux/avatar-react-mui";
import {
    CommentCounter,
    ContributorAvatars,
    ProjectCard as SharedProjectCard,
    ProjectCardDescription,
    ProjectCardThumbnailArea,
    ProjectCardTitle,
    UsageCounter,
} from "@buildwithflux/project-cards";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import DocumentThumb from "../common/DocumentThumb";
import {FluxExpandCollapse} from "../common/FluxExpandCollapse";
import MomentWithTooltip from "../common/MomentWithTooltip";

import type {ProjectHit} from "./ProjectHit";
import StarMetricControl from "./StarMetricControl";

const AVATAR_SIZE = 20;
const CONTRIBUTOR_AVATAR_SIZE = 24;

function lastModifiedTooltip(formattedDate: string) {
    return `Last modified: ${formattedDate}`;
}

function renderContributorAvatar(uid: string) {
    return <FluxAvatar key={uid} uid={uid} size={CONTRIBUTOR_AVATAR_SIZE} />;
}

export default function UnifiedProjectCard({project}: {project: ProjectHit}) {
    // Owner is rendered as the leading avatar already, so drop it from the extras.
    const contributorUids = project.contributorUids.filter((uid) => uid && uid !== project.ownerUid);

    return (
        <SharedProjectCard
            component="li"
            thumbnailArea={
                <ProjectCardThumbnailArea>
                    <Link href={project.url}>
                        <DocumentThumb thumbUrl={project.thumbUrl} documentName={project.name} size={100} />
                    </Link>
                </ProjectCardThumbnailArea>
            }
            title={<ProjectCardTitle href={project.url}>{project.name}</ProjectCardTitle>}
            description={
                project.description ? (
                    <ProjectCardDescription>
                        <FluxExpandCollapse
                            previewHeight={
                                60 // three lines at body2
                            }
                        >
                            {project.description}
                        </FluxExpandCollapse>
                    </ProjectCardDescription>
                ) : undefined
            }
            authorRow={
                <>
                    <Box
                        sx={{
                            display: "inline-block",
                            width: "20px", // So it doesn't resize after the image loads
                            verticalAlign: "-5px", // To align with text baseline
                        }}
                    >
                        <Link href={`/${project.ownerHandle}`} aria-label={project.ownerHandle}>
                            <FluxAvatar uid={project.ownerUid} size={AVATAR_SIZE} alt={project.ownerHandle} />
                        </Link>
                    </Box>
                    <Box sx={{display: "inline"}}>
                        <Typography variant="body2" noWrap sx={{display: "inline"}}>
                            <Tooltip title={project.ownerHandle} placement="top">
                                <Link
                                    href={`/${project.ownerHandle}`}
                                    sx={{
                                        wordWrap: "break-word",
                                        hyphens: "none",
                                        textOverflow: "ellipsis",
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        color: "primary.main",
                                    }}
                                >
                                    {project.ownerHandle}
                                </Link>
                            </Tooltip>
                        </Typography>
                    </Box>
                    {project.ownerVerified ? (
                        <CheckCircleIcon sx={{fontSize: 12, flexShrink: 0, color: "primary.main"}} />
                    ) : null}
                    {contributorUids.length > 0 && (
                        <>
                            <Typography variant="body2" sx={{display: "inline"}} color="textSecondary">
                                +
                            </Typography>
                            <ContributorAvatars
                                contributorUids={contributorUids}
                                renderAvatar={renderContributorAvatar}
                            />
                        </>
                    )}
                    {project.updatedAt ? (
                        <Typography variant="body2" color="textSecondary" sx={{display: "inline"}}>
                            <MomentWithTooltip date={project.updatedAt} customTooltipRenderer={lastModifiedTooltip} />
                        </Typography>
                    ) : null}
                </>
            }
            metrics={
                <>
                    <StarMetricControl documentUid={project.uid} count={project.starCount} />
                    <UsageCounter count={project.usageCount} color="textSecondary" />
                    <CommentCounter count={project.commentCount} color="textSecondary" />
                </>
            }
        />
    );
}
